import {useCallback} from "react";
import Cookies from "js-cookie";
import {
    FIREBASE_VAPID_KEY,
    getFcmServiceWorkerUrl,
    getMessagingIfSupported,
} from "@/lib/firebase/config";
import {FCM_SW_SCOPE, FCM_TOKEN_STORAGE_KEY} from "@/lib/firebase/localToken";
import {useRegisterPushTokenMutation} from "@/lib/api/auth/pushTokensApi";

/**
 * Shared web-push (FCM) registration — AC-649. Used by FcmInitializer (silent
 * re-sync of an already-granted token) and the opt-in CTA (after the user grants
 * permission). Registration = register the FCM service worker, mint an FCM token
 * and persist it on the backend. Local token teardown lives in
 * lib/firebase/localToken (a leaf module with no RTK import), which also owns
 * FCM_SW_SCOPE so both registration and teardown resolve the same worker.
 *
 * `firebase/messaging` is `import()`-ed inside the callback, not statically —
 * this hook is used by AvatarPopoverContent and EnablePushNotificationsPrompt,
 * both rendered on every authenticated page, so a static SDK import here would
 * ship it in the shell whether or not the visitor ever touches push (AC-803).
 */

export {FCM_SW_SCOPE};

/** Returns a callback that mints and backend-registers the FCM token. */
export function useWebPushRegistration() {
    const [registerPushToken] = useRegisterPushTokenMutation();

    return useCallback(async () => {
        const swReg = await navigator.serviceWorker.register(
            getFcmServiceWorkerUrl(),
            {scope: FCM_SW_SCOPE, updateViaCache: "none"}
        );
        const messaging = await getMessagingIfSupported();
        if (!messaging) return;

        const {getToken} = await import("firebase/messaging");
        const token = await getToken(messaging, {
            vapidKey: FIREBASE_VAPID_KEY,
            serviceWorkerRegistration: swReg,
        });
        if (!token) return;

        localStorage.setItem(FCM_TOKEN_STORAGE_KEY, token);
        // Include the device UUID (FE `deviceId` cookie == backend `did`) when
        // present so server-side logout-by-device / rotation cleanup covers web.
        const deviceId = Cookies.get("deviceId");
        await registerPushToken({
            token,
            platform: "web",
            ...(deviceId ? {deviceId} : {}),
        }).unwrap();
    }, [registerPushToken]);
}
